import { useState } from "react";
import { mediaUrl } from "@/lib/api";

/**
 * Resilient cover image for portfolio cards / heroes.
 * - Uses `src` (cover), falling back to `fallbackSrc` (e.g. first gallery image).
 * - If both fail to load, shows a branded placeholder tile with the project title.
 * - `natural` (default false): when true the image keeps its ORIGINAL aspect ratio
 *   (w-full, h-auto, not cropped). When false it fills a fixed-ratio frame (object-cover).
 */
export default function CoverImage({
  src,
  fallbackSrc = "",
  alt = "",
  title = "",
  className = "",
  imgClassName = "",
  loading = "lazy",
  natural = false,
  testId,
}) {
  const primary = mediaUrl(src);
  const secondary = mediaUrl(fallbackSrc);
  const candidates = [primary, secondary].filter(
    (v, i, arr) => v && arr.indexOf(v) === i
  );
  const [idx, setIdx] = useState(0);
  const current = candidates[idx];

  const handleError = () => {
    if (idx < candidates.length - 1) setIdx(idx + 1);
    else setIdx(candidates.length);
  };

  const showPlaceholder = candidates.length === 0 || idx >= candidates.length;

  if (showPlaceholder) {
    return (
      <div
        className={`w-full flex items-center justify-center bg-neutral-900 ${
          natural ? "aspect-[4/5]" : "h-full"
        } ${className}`}
        data-testid={testId ? `${testId}-placeholder` : undefined}
      >
        <span className="font-editorial text-ivory/40 text-2xl md:text-3xl px-6 text-center leading-tight">
          {title || "Oereshaus"}
        </span>
      </div>
    );
  }

  const defaultImgClass = natural
    ? "w-full h-auto block"
    : "w-full h-full object-cover";

  return (
    <img
      src={current}
      alt={alt || title}
      loading={loading}
      onError={handleError}
      className={imgClassName || defaultImgClass}
      data-testid={testId}
    />
  );
}
